import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogContent,
	Link,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { getProfileList, newReferenceRequest } from '../../actions/referenceRequestActions.js';
import routes from '../../constants/routes';
import ReferenceRequest from '../../pages/Requests/request-form';
import { track } from '../../utils/analytics';
import G2ReviewModal, { shouldShowG2ReviewModal } from '../common/G2ReviewModal';
import Navigation from '../Navigation';

const StyledBillingAlert = styled.div`
	background-color: #c53929;
	border-top: 0;
	border-left: 0;
	border-right: 0;
	color: #fff;
	font-size: 0.875rem;
	padding: 10px 10px 10px 0;
	text-align: center;
`;

const SkipToMainContentLink = styled(Link)(({ theme }) => ({
	position: 'absolute',
	top: '-40px',
	left: 0,
	background: theme.palette.secondary.main,
	color: theme.palette.common.white,
	padding: '8px',
	zIndex: 100,
	transition: 'top 0.3s',
	textDecoration: 'none',

	':focus': {
		top: 0,
		color: theme.palette.primary[300],
	},
}));

function isUsingInternetExplorer() {
	var ua = window.navigator.userAgent;
	var msie = ua.indexOf('MSIE ');
	// const version = parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)));

	if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./)) {
		alert(
			'We no longer support your current browser (Internet Explorer) and you may experience a loss of functionality when navigating our site. We advise that you switch to a modern browser (chrome, firefox, edge) and try again. Thank you!'
		);
	}
}

export const LoggedInLayout = ({
	getProfileList,
	isSuperAdmin,
	overdueInvoiceDays,
	orgRequestsCreated,
	trialCredits,
}) => {
	const [open, setOpen] = useState(false);
	const [showG2ReviewModal, setShowG2ReviewModal] = useState(false);
	const { formatMessage } = useIntl();
	const location = useLocation();
	const theme = useTheme();
	const navigate = useNavigate();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	// const showDashboardAlert =
	//     location.pathname === routes.employer && !localStorage.getItem('hideDashboardAlert');

	const handleClose = () => {
		if (open) {
			setOpen(false);
			track('Closed request form');
		}
	};

	const handleOpen = () => {
		setOpen(true);
		track('Opened request form', { isMobile: fullScreen });
	};

	useEffect(() => {
		isUsingInternetExplorer();
	}, []);

	useEffect(() => {
		open && getProfileList();
	}, [open]);

	useEffect(() => {
		open && handleClose();
	}, [location.pathname]);

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const newRequest = searchParams.get('newRequest');
		const tour = searchParams.get('tourId');

		if (newRequest == 1) {
			track('Opened via Ref.New');
			handleOpen();
		}

		if (tour && tour === 'new-request') {
			navigate(routes.settings.tours);
		}
	}, []);

	useEffect(() => {
		// Use the function from the modal component
		if (shouldShowG2ReviewModal(orgRequestsCreated)) {
			setShowG2ReviewModal(true);
		}
	}, []);

	// Check if trial credits banner should be shown
	const shouldShowTrialCreditsBanner =
		trialCredits && trialCredits.count > 0 && trialCredits.days_until_expiry !== null;

	// const hideDashboardAlert = () => localStorage.setItem('hideDashboardAlert', true);

	return (
		<Box
			id="logged_in_layout"
			sx={{
				// Allow main content to scroll on desktop; overflow hidden clipped long pages (e.g. Reporting).
				overflow: 'auto',
				WebkitOverflowScrolling: 'touch',
			}}
		>
			<SkipToMainContentLink href="#main-content">Skip to Main Content</SkipToMainContentLink>
			{overdueInvoiceDays > 45 && (
				<StyledBillingAlert>
					{formatMessage({ id: 'Settings.billing.overdueInvoicesMsg' })}
				</StyledBillingAlert>
			)}
			{shouldShowTrialCreditsBanner && (
				<Alert
					severity="info"
					variant="filled"
					action={
						<Button
							variant="contained"
							size="small"
							href="https://media.refnow.com/meetings/demo-meet/refnow-initial-meeting"
							target="_blank"
							rel="noopener noreferrer"
							sx={{
								backgroundColor: '#fff',
								color: theme.palette.secondary.main,
								'&:hover': {
									backgroundColor: theme.palette.grey[100],
								},
								'&:focus': {
									outline: `2px solid ${theme.palette.secondary.main}`,
									outlineOffset: '2px',
								},
							}}
						>
							{formatMessage({ id: 'Layout.bookADemo' })}
						</Button>
					}
					sx={{
						borderRadius: '0 !important',
						backgroundColor: theme.palette.primary.main,
						color: '#fff',
						'& .MuiAlert-icon': {
							color: '#fff',
						},
						'& .MuiAlert-action': {
							alignItems: 'center',
							padding: 0,
						},
					}}
				>
					{formatMessage(
						{
							id: 'Layout.trialCreditsBanner',
						},
						{
							count: trialCredits.count,
							days: trialCredits.days_until_expiry,
						}
					)}
				</Alert>
			)}
			<Navigation newRefRequestBtnClick={handleOpen} closeRefRequestPanel={handleClose} />
			{/* {showDashboardAlert && (
                <Alert
                    severity="info"
                    variant="filled"
                    onClose={hideDashboardAlert}
                    sx={{ borderRadius: '0 !important', bgcolor: 'secondary.100' }}
                >
                    {formatMessage(
                        { id: 'Dashboards.labels.newUpdateMessage' },
                        {
                            a: (msg) => (
                                <Link href={routes.employerDashboard} onClick={hideDashboardAlert}>
                                    {msg}
                                </Link>
                            ),
                        }
                    )}
                </Alert>
            )} */}
			<Outlet />
			{showG2ReviewModal && (
				<G2ReviewModal
					open={showG2ReviewModal}
					onClose={() => setShowG2ReviewModal(false)}
				/>
			)}
			<Dialog
				open={open}
				fullScreen={fullScreen}
				fullWidth
				maxWidth="lg"
				disableEscapeKeyDown
				keepMounted
				onClose={handleClose}
				aria-modal="true"
				aria-labelledby="new-request-form"
				aria-describedby="create-new-reference-request"
			>
				<DialogContent sx={{ padding: 0, overflowY: 'hidden' }}>
					<ReferenceRequest
						isMobile={fullScreen}
						open={open}
						onClose={handleClose}
						isSuperAdmin={isSuperAdmin}
					/>
				</DialogContent>
			</Dialog>
		</Box>
	);
};

const mapStateToProps = ({ user, locales }) => ({
	creditBal: user.creditBal,
	isSuperAdmin: user.isSuperAdmin,
	locale: locales.locale,
	orgRequestsCreated: user.org_requests_created,
	overdueInvoiceDays: user.most_overdue_invoice_days,
	trialCredits: user.trial_credits,
});

const mapDispatchToProps = {
	getProfileList,
	newReferenceRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoggedInLayout);
